export type DashboardDateRangeMode =
  | 'calendarYearToDate'
  | 'calendarQuarterToDate'
  | 'calendarMonthToDate'
  | 'rollingDays';

export interface DashboardDateRangeConfiguration {
  Mode: DashboardDateRangeMode;
  RollingPeriodDays?: number;
}

export interface DashboardDateRange {
  StartDate: string;
  EndDate: string;
}

export const DEFAULT_DASHBOARD_DATE_RANGE: DashboardDateRangeConfiguration = {
  Mode: 'calendarYearToDate'
};

const ukDateFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

/**
 * Creates an inclusive dashboard range from a date-range mode. Calendar
 * boundaries are evaluated in Europe/London even when the browser is running
 * in another timezone.
 */
export function dashboardDateRange(
  configuration: DashboardDateRangeConfiguration = DEFAULT_DASHBOARD_DATE_RANGE,
  now: Date = new Date()
): DashboardDateRange {
  const today = ukCalendarDate(now);
  const endDate = formatCalendarDate(today.year, today.month, today.day);

  switch (configuration.Mode) {
    case 'calendarMonthToDate':
      return {
        StartDate: formatCalendarDate(today.year, today.month, 1),
        EndDate: endDate
      };
    case 'calendarQuarterToDate':
      return {
        StartDate: formatCalendarDate(today.year, Math.floor((today.month - 1) / 3) * 3 + 1, 1),
        EndDate: endDate
      };
    case 'rollingDays': {
      const rollingPeriodDays = normaliseRollingPeriodDays(configuration.RollingPeriodDays);
      const start = new Date(Date.UTC(today.year, today.month - 1, today.day));
      start.setUTCDate(start.getUTCDate() - (rollingPeriodDays - 1));
      return {
        StartDate: formatCalendarDate(start.getUTCFullYear(), start.getUTCMonth() + 1, start.getUTCDate()),
        EndDate: endDate
      };
    }
    case 'calendarYearToDate':
    default:
      return {
        StartDate: formatCalendarDate(today.year, 1, 1),
        EndDate: endDate
      };
  }
}

function ukCalendarDate(date: Date): { year: number; month: number; day: number } {
  const parts = ukDateFormatter.formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find(part => part.type === type)?.value);

  return {
    year: value('year'),
    month: value('month'),
    day: value('day')
  };
}

function normaliseRollingPeriodDays(value?: number): number {
  return Number.isInteger(value) && (value as number) > 0
    ? value as number
    : 30;
}

function formatCalendarDate(year: number, month: number, day: number): string {
  return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}
